import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Badge, Button, ButtonGroup, Card, Col, Row, Spinner, ToggleButton } from 'react-bootstrap'
import { apiClient } from '../services/api/client'
import { getAutoRefreshIntervalMs, useAutoRefresh } from '../hooks/useAutoRefresh'

const normalizeSubcode = (value) => {
  return (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
}

const formatCurrentDate = () => {
  const date = new Date()
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`
}

const formatAutoRefresh = () => {
  const intervalMs = getAutoRefreshIntervalMs()
  if (intervalMs <= 0) return 'Automatické obnovení vypnuto.'
  const seconds = Math.round(intervalMs / 1000)
  if (seconds % 60 !== 0) return `Automatické obnovení každých ${seconds} s.`
  const minutes = seconds / 60
  if (minutes === 1) return 'Automatické obnovení každou minutu.'
  if (minutes >= 2 && minutes <= 4) return `Automatické obnovení každé ${minutes} minuty.`
  return `Automatické obnovení každých ${minutes} minut.`
}

const DashboardMechanic = () => {
  const navigate = useNavigate()
  const [orders, setOrders] = useState([])
  const [strediskoNazev, setStrediskoNazev] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('dilna')

  const filteredOrders = useMemo(() => (orders || []).filter((order) => {
    if (statusFilter === 'all') return true
    return normalizeSubcode(order?.stav) === statusFilter
  }), [orders, statusFilter])

  const statusMeta = (value) => {
    const normalized = normalizeSubcode(value)
    if (normalized === 'dilna') return { label: 'Na dílně', variant: 'warning', textClass: 'text-dark' }
    if (normalized === 'provedeno') return { label: 'Provedeno', variant: 'danger', textClass: '' }
    return { label: value || 'Bez stavu', variant: 'secondary', textClass: '' }
  }

  const loadOrders = async () => {
    try {
      setLoading(true)
      setError('')
      const data = await apiClient.get('/objednavky/mechanik/dilna')
      if (Array.isArray(data)) {
        setOrders(data)
        setStrediskoNazev('')
      } else {
        setOrders(Array.isArray(data?.orders) ? data.orders : [])
        setStrediskoNazev(String(data?.strediskoNazev || '').trim())
      }
    } catch (e) {
      setOrders([])
      setStrediskoNazev('')
      setError(e.message || 'Nepodařilo se načíst objednávky.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrders()
  }, [])

  useAutoRefresh(loadOrders)

  return (
    <div className='p-2'>
      <div className="mb-3">
        <div>
          <h4 className="mb-0">Zakázky</h4>
          <div className="small text-muted">Přehled zakázek pro aktuální den ({formatCurrentDate()}). {formatAutoRefresh()}</div>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
      <ButtonGroup aria-label="Filtr stavu objednávek">
        {[
          { value: 'dilna', label: 'Na dílně', icon: 'bi-tools' },
          { value: 'provedeno', label: 'Provedené', icon: 'bi-check2-circle' },
          { value: 'all', label: 'Vše', icon: 'bi-grid' }
        ].map((filter) => (
          <ToggleButton
            key={filter.value}
            id={`mechanic-filter-${filter.value}`}
            type="radio"
            name="mechanic-order-filter"
            value={filter.value}
            variant="outline-primary"
            className="btn-lg"
            checked={statusFilter === filter.value}
            onChange={(event) => setStatusFilter(event.currentTarget.value)}
          >
            <i className={`bi ${filter.icon} me-2`}></i>{filter.label}
          </ToggleButton>
        ))}
      </ButtonGroup>
      <Button className='btn-lg ms-auto' type="button" variant="outline-primary" onClick={loadOrders} disabled={loading}>
        <i className="bi bi-arrow-clockwise me-2"></i>Obnovit
      </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <Row className="g-1">
          {filteredOrders.length === 0 && (
            <Col xs={12}>
              <Alert variant="secondary" className="mb-0">
                Pro dnešní den nejsou v tomto filtru žádné objednávky.
              </Alert>
            </Col>
          )}
          {filteredOrders.map((o) => {
            const meta = statusMeta(o.stav)
            return (
            <Col xs={6} key={o.idobj}>
              <Card
                className="mechanic-order-card h-100"
                role="button"
                onClick={() => navigate(`/mechanic/order/${o.idobj}`)}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="small text-muted mb-1">SPZ</div>
                      <div className="fs-3 fw-bold lh-1">{o.spz || '-'}</div>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-2">
                      <Badge bg="dark" pill className="px-3 py-2">Stání {o.stani || '-'}</Badge>
                      <Badge bg={meta.variant} className={`px-3 py-2 ${meta.textClass}`}>{meta.label}</Badge>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="d-flex align-items-center gap-2">
                      {/* <i className="bi bi-person-square fs-2 lh-1"></i> */}
                      <div>                        
                        <div className="small text-muted">Zákazník</div>
                        <div className="fw-semibold">{o.zakaznik || '-'}</div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            )
          })}
        </Row>
      )}
    </div>
  )
}

export default DashboardMechanic
